import { useState, type FormEvent } from "react"
import { format, parse } from "date-fns"
import { ArrowLeft, FileX2, Search } from "lucide-react"
import type { DateRange } from "react-day-picker"
import { NavLink } from "react-router-dom"
import type { OtkDocumentSearchResult, OtkDocumentsSearchInput, OtkOwnerType } from "shared"

import { DataTable } from "@/components/DataTable"
import { DateRangePicker } from "@/components/DateRangePicker"
import { buildDocumentSearchColumns } from "@/components/documents/columns"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Field, FieldError, FieldLabel, FieldRow } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { searchOtkDocuments } from "@/lib/api/documents"
import type { OtkBranch } from "@/lib/api/subjects"
import {
  documentTabLabels,
  documentTabStatuses,
  documentTabStatusToBackend,
  documentTypeToBackend,
  documentTypes,
  type DocumentTabStatus,
  type DocumentType,
} from "@/lib/documents"
import { vehicleBrands, vehicleKinds, vehicleModels } from "@/lib/documentEditor"

function toggle<T>(current: T[], value: T): T[] {
  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value]
}

// `Типи Документа` must always carry at least one selection —
// `otkDocumentsSearchInputSchema.types` is `min(1)` when present, and unlike
// `Пункти ОТК` (still omitted from the request when every branch is
// checked, read server-side as "no restriction"), it's always sent as
// exactly what's checked. Unchecking the last remaining box is a no-op
// instead of producing an empty, unsendable array. `Статуси` doesn't use
// this — unchecking every box there is allowed and omits `statuses` from
// the request entirely (server-side "no restriction"), same as `Пункти ОТК`.
function toggleRequireAtLeastOne<T>(current: T[], value: T): T[] {
  const next = toggle(current, value)
  return next.length > 0 ? next : current
}

// Every free-text search field requires at least 4 characters before it
// participates in the search. This mirrors `otkDocumentsSearchInputSchema`'s
// `vehicle.plate`/`.vin`/`.registrationCertificate` (each `min(4)`) and is
// applied the same way to `documentSequence`, `owner.name`, and
// `owner.taxId` for a consistent search UX, even where the backend itself
// would accept a shorter value (`owner.name` is `min(2)`).
const MIN_SEARCH_FIELD_LENGTH = 4
function tooShort(value: string): boolean {
  const trimmed = value.trim()
  return trimmed.length > 0 && trimmed.length < MIN_SEARCH_FIELD_LENGTH
}

/**
 * documents.md's `DocumentAdvancedSearch` — route `/documents/search`.
 * Submitting builds an `OtkDocumentsSearchInput` from the selected values and
 * calls `POST /otk/documents/search` (otk-documents.ts), rendering the
 * response inline below the form instead of navigating away. `Типи
 * Документа` is always sent as-is (never omitted, even when every checkbox
 * is checked) — leaving every type checked used to be read by the backend
 * as "no restriction," which would silently search backend types the
 * checkboxes don't offer. `Статуси` sends exactly what's checked when
 * anything is checked, and omits the field (server-side "no restriction")
 * only once every box is unchecked; `Пункти ОТК` omits once every branch is
 * checked instead — same "omit == no restriction" idea, different trigger
 * per field. The results
 * table (`buildDocumentSearchColumns`) shows every field the search can
 * filter on — `registrationCertificate`/`vehicleType`/`ownerType`/
 * `ownerName`/`ownerTaxId` included — resolving `OPEN-QUESTIONS.md` item 102
 * now that the backend carries those fields end to end
 * (`OtkDocumentSearchResult`).
 */
export function DocumentAdvancedSearch({ branches }: { branches: OtkBranch[] }) {
  const allPunktIds = branches.map((branch) => branch.registryId)
  const punktNameById = Object.fromEntries(
    branches.map((branch) => [branch.registryId, branch.name])
  )

  const [punktIds, setPunktIds] = useState<string[]>(allPunktIds)
  const [number, setNumber] = useState("")
  const [signedAfter, setSignedAfter] = useState("")
  const [signedTo, setSignedTo] = useState("")
  const [types, setTypes] = useState<DocumentType[]>([...documentTypes])
  const [statuses, setStatuses] = useState<DocumentTabStatus[]>([
    ...documentTabStatuses,
  ])
  const [plate, setPlate] = useState("")
  const [vin, setVin] = useState("")
  const [registrationCertificate, setRegistrationCertificate] = useState("")
  const [brand, setBrand] = useState("all")
  const [model, setModel] = useState("all")
  const [vehicleType, setVehicleType] = useState("all")
  const [ownerType, setOwnerType] = useState("all")
  const [ownerName, setOwnerName] = useState("")
  const [ownerTaxId, setOwnerTaxId] = useState("")
  const [results, setResults] = useState<OtkDocumentSearchResult[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  // The exact input the currently-shown `results` came from, minus `cursor`
  // — `handleLoadMore` reuses it as-is (not re-derived from the live form
  // fields, which may have changed since submit) so paging in doesn't
  // silently switch to different filters mid-scroll.
  const [searchInput, setSearchInput] = useState<OtkDocumentsSearchInput | null>(null)
  const [nextPage, setNextPage] = useState<string | null>(null)
  const selectedBrand = vehicleBrands.find((item) => item.brandName === brand)
  const modelOptions = selectedBrand
    ? vehicleModels.filter((item) => item.brandId === selectedBrand.brandId)
    : []
  const signedRange: DateRange | undefined = signedAfter
    ? {
        from: parse(signedAfter, "yyyy-MM-dd", new Date()),
        to: signedTo ? parse(signedTo, "yyyy-MM-dd", new Date()) : undefined,
      }
    : undefined
  function changeSignedRange(range: DateRange | undefined) {
    setSignedAfter(range?.from ? format(range.from, "yyyy-MM-dd") : "")
    setSignedTo(range?.to ? format(range.to, "yyyy-MM-dd") : "")
  }
  const numberError = tooShort(number) ? "Мінімум 4 символи" : undefined
  const plateError = tooShort(plate) ? "Мінімум 4 символи" : undefined
  const vinError = tooShort(vin) ? "Мінімум 4 символи" : undefined
  const registrationCertificateError = tooShort(registrationCertificate)
    ? "Мінімум 4 символи"
    : undefined
  const ownerNameError = tooShort(ownerName) ? "Мінімум 4 символи" : undefined
  const ownerTaxIdError = tooShort(ownerTaxId) ? "Мінімум 4 символи" : undefined
  const hasFieldErrors = Boolean(
    numberError ||
      plateError ||
      vinError ||
      registrationCertificateError ||
      ownerNameError ||
      ownerTaxIdError
  )

  function buildSearchInput(): OtkDocumentsSearchInput {
    const vehicle: NonNullable<OtkDocumentsSearchInput["vehicle"]> = {}
    if (plate.trim().length >= MIN_SEARCH_FIELD_LENGTH) vehicle.plate = plate.trim()
    if (vin.trim().length >= MIN_SEARCH_FIELD_LENGTH) vehicle.vin = vin.trim()
    if (registrationCertificate.trim().length >= MIN_SEARCH_FIELD_LENGTH) {
      vehicle.registrationCertificate = registrationCertificate.trim()
    }
    if (brand !== "all") vehicle.brand = brand
    if (model !== "all") vehicle.model = model
    if (vehicleType !== "all") vehicle.type = vehicleType

    const owner: NonNullable<OtkDocumentsSearchInput["owner"]> = {}
    if (ownerType !== "all") owner.type = ownerType as OtkOwnerType
    if (ownerName.trim().length >= MIN_SEARCH_FIELD_LENGTH) owner.name = ownerName.trim()
    if (ownerTaxId.trim().length >= MIN_SEARCH_FIELD_LENGTH) owner.taxId = ownerTaxId.trim()

    return {
      ...(punktIds.length > 0 && punktIds.length < allPunktIds.length
        ? { punktIds }
        : {}),
      ...(number.trim().length >= MIN_SEARCH_FIELD_LENGTH
        ? { documentSequence: Number(number) }
        : {}),
      ...(signedAfter ? { signedFrom: signedAfter } : {}),
      ...(signedTo ? { signedTo } : {}),
      types: types.map((type) => documentTypeToBackend[type]),
      ...(statuses.length > 0
        ? { statuses: statuses.flatMap((status) => documentTabStatusToBackend[status]) }
        : {}),
      ...(Object.keys(vehicle).length > 0 ? { vehicle } : {}),
      ...(Object.keys(owner).length > 0 ? { owner } : {}),
      // `otkDocumentsSearchInputSchema`'s own defaults (`.default(...)`)  —
      // spelled out here because `OtkDocumentsSearchInput` is the schema's
      // *output* type (`z.infer`), where a defaulted field is required, not
      // optional like it is for an actual caller.
      pageSize: 20,
      sort: "DESC",
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (hasFieldErrors) return

    const input = buildSearchInput()

    setIsSearching(true)
    setSearchError(null)
    try {
      const page = await searchOtkDocuments(input)
      setResults(page.data)
      setSearchInput(input)
      setNextPage(page.nextPage)
      setHasSearched(true)
    } catch {
      setResults([])
      setSearchInput(null)
      setNextPage(null)
      setHasSearched(true)
      setSearchError("Не вдалося виконати пошук. Спробуйте ще раз.")
    } finally {
      setIsSearching(false)
    }
  }

  async function handleLoadMore() {
    if (!nextPage || !searchInput || isLoadingMore || searchParamsChanged) return

    setIsLoadingMore(true)
    setSearchError(null)
    try {
      const page = await searchOtkDocuments({ ...searchInput, cursor: nextPage })
      setResults((current) => [...current, ...page.data])
      setNextPage(page.nextPage)
    } catch {
      setSearchError("Не вдалося завантажити наступну сторінку. Спробуйте ще раз.")
    } finally {
      setIsLoadingMore(false)
    }
  }

  // The form's live values have drifted from whatever `searchInput` produced
  // the results currently on screen — paging further with the old cursor
  // would silently keep applying the stale filters, so "Завантажити ще"
  // disables until the user re-submits.
  const searchParamsChanged =
    searchInput === null || JSON.stringify(buildSearchInput()) !== JSON.stringify(searchInput)

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">Розширений Пошук</h1>
      <Button asChild className="w-fit" variant="link">
        <NavLink to="/documents">
          <ArrowLeft data-icon="inline-start" />
          Назад
        </NavLink>
      </Button>

      <Card>
        <CardContent>
          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <Field length="full-size">
              <FieldLabel>Пункти ОТК</FieldLabel>
              <div aria-label="Пункти ОТК" className="flex flex-wrap gap-4" role="group">
                {branches.map((branch) => (
                  <label className="flex items-center gap-2 text-sm" key={branch.registryId}>
                    <Checkbox
                      checked={punktIds.includes(branch.registryId)}
                      onCheckedChange={() =>
                        setPunktIds((current) => toggle(current, branch.registryId))
                      }
                    />
                    {branch.name}
                  </label>
                ))}
              </div>
            </Field>

            <FieldRow>
              <Field data-invalid={Boolean(numberError)} length="4x">
                <FieldLabel htmlFor="advanced-search-number">Номер документа</FieldLabel>
                <Input
                  aria-invalid={Boolean(numberError)}
                  id="advanced-search-number"
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(event) => setNumber(event.target.value.replace(/\D/g, ""))}
                  placeholder="000124"
                  value={number}
                />
                {numberError && <FieldError>{numberError}</FieldError>}
              </Field>

              <Field controlLength="4x" length="6x">
                <FieldLabel>Дата підписання</FieldLabel>
                <DateRangePicker
                  onChange={changeSignedRange}
                  placeholder="Оберіть дату"
                  value={signedRange}
                />
              </Field>
            </FieldRow>

            <p className="text-sm font-medium">Транспортний засіб</p>
            <FieldRow>
              <Field data-invalid={Boolean(plateError)} length="4x">
                <FieldLabel htmlFor="advanced-search-plate">Номерний знак</FieldLabel>
                <Input
                  aria-invalid={Boolean(plateError)}
                  id="advanced-search-plate"
                  maxLength={8}
                  onChange={(event) => setPlate(event.target.value)}
                  placeholder="AA1234AA"
                  value={plate}
                />
                {plateError && <FieldError>{plateError}</FieldError>}
              </Field>

              <Field data-invalid={Boolean(vinError)} length="4x">
                <FieldLabel htmlFor="advanced-search-vin">VIN-код</FieldLabel>
                <Input
                  aria-invalid={Boolean(vinError)}
                  id="advanced-search-vin"
                  maxLength={17}
                  onChange={(event) => setVin(event.target.value)}
                  placeholder="17 символів"
                  value={vin}
                />
                {vinError && <FieldError>{vinError}</FieldError>}
              </Field>

              <Field data-invalid={Boolean(registrationCertificateError)} length="4x">
                <FieldLabel htmlFor="advanced-search-registration-certificate">
                  Свідоцтво про реєстрацію
                </FieldLabel>
                <Input
                  aria-invalid={Boolean(registrationCertificateError)}
                  id="advanced-search-registration-certificate"
                  onChange={(event) => setRegistrationCertificate(event.target.value)}
                  placeholder="Серія і номер свідоцтва"
                  value={registrationCertificate}
                />
                {registrationCertificateError && (
                  <FieldError>{registrationCertificateError}</FieldError>
                )}
              </Field>
            </FieldRow>

            <FieldRow>
              <Field length="4x">
                <FieldLabel htmlFor="advanced-search-brand">Марка</FieldLabel>
                <Select
                  onValueChange={(value) => {
                    setBrand(value)
                    setModel("all")
                  }}
                  value={brand}
                >
                  <SelectTrigger id="advanced-search-brand" length="4x">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Усі</SelectItem>
                    {vehicleBrands.map((item) => (
                      <SelectItem key={item.brandId} value={item.brandName}>
                        {item.brandName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field length="4x">
                <FieldLabel htmlFor="advanced-search-model">Модель</FieldLabel>
                <Select
                  disabled={!selectedBrand}
                  onValueChange={setModel}
                  value={model}
                >
                  <SelectTrigger id="advanced-search-model" length="4x">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Усі</SelectItem>
                    {modelOptions.map((item) => (
                      <SelectItem key={item.modelId} value={item.modelName}>
                        {item.modelName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field length="4x">
                <FieldLabel htmlFor="advanced-search-vehicle-type">Тип ТЗ</FieldLabel>
                <Select onValueChange={setVehicleType} value={vehicleType}>
                  <SelectTrigger id="advanced-search-vehicle-type" length="4x">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Усі</SelectItem>
                    {vehicleKinds.map((item) => (
                      <SelectItem key={item.kindId} value={item.kind}>
                        {item.kind}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </FieldRow>

            <p className="text-sm font-medium">Власник</p>
            <FieldRow>
              <Field length="4x">
                <FieldLabel htmlFor="advanced-search-owner-type">
                  Тип власника
                </FieldLabel>
                <Select onValueChange={setOwnerType} value={ownerType}>
                  <SelectTrigger id="advanced-search-owner-type" length="4x">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Усі</SelectItem>
                    <SelectItem value="INDIVIDUAL">Фізична особа</SelectItem>
                    <SelectItem value="LEGAL_ENTITY">
                      Юридична особа
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field data-invalid={Boolean(ownerNameError)} length="4x">
                <FieldLabel htmlFor="advanced-search-owner-name">
                  ПІБ / Назва організації
                </FieldLabel>
                <Input
                  aria-invalid={Boolean(ownerNameError)}
                  id="advanced-search-owner-name"
                  onChange={(event) => setOwnerName(event.target.value)}
                  value={ownerName}
                />
                {ownerNameError && <FieldError>{ownerNameError}</FieldError>}
              </Field>

              <Field data-invalid={Boolean(ownerTaxIdError)} length="4x">
                <FieldLabel htmlFor="advanced-search-owner-tax-id">
                  ІПН / ЄДРПОУ
                </FieldLabel>
                <Input
                  aria-invalid={Boolean(ownerTaxIdError)}
                  id="advanced-search-owner-tax-id"
                  onChange={(event) => setOwnerTaxId(event.target.value)}
                  value={ownerTaxId}
                />
                {ownerTaxIdError && <FieldError>{ownerTaxIdError}</FieldError>}
              </Field>
            </FieldRow>

            <Field length="full-size">
              <FieldLabel>Типи Документа</FieldLabel>
              <div aria-label="Типи Документа" className="flex flex-wrap gap-4" role="group">
                {documentTypes.map((type) => (
                  <label className="flex items-center gap-2 text-sm" key={type}>
                    <Checkbox
                      checked={types.includes(type)}
                      onCheckedChange={() =>
                        setTypes((current) => toggleRequireAtLeastOne(current, type))
                      }
                    />
                    {type}
                  </label>
                ))}
              </div>
            </Field>

            <Field length="full-size">
              <FieldLabel>Статуси</FieldLabel>
              <div aria-label="Статуси" className="flex flex-wrap gap-4" role="group">
                {documentTabStatuses.map((status) => (
                  <label className="flex items-center gap-2 text-sm" key={status}>
                    <Checkbox
                      checked={statuses.includes(status)}
                      onCheckedChange={() =>
                        setStatuses((current) => toggle(current, status))
                      }
                    />
                    {documentTabLabels[status]}
                  </label>
                ))}
              </div>
            </Field>

            <Button className="w-fit" disabled={hasFieldErrors} type="submit">
              <Search data-icon="inline-start" />
              Пошук
            </Button>
          </form>
        </CardContent>
      </Card>

      {searchError && <p className="text-sm text-destructive">{searchError}</p>}

      {(isSearching || hasSearched) && (
        <Card className="min-w-0">
          <CardContent className="min-w-0">
            {!isSearching && results.length === 0 ? (
              <div className="flex min-h-40 flex-col items-center justify-center rounded-xl border border-dashed text-center">
                <FileX2 className="mb-3 size-10 text-muted-foreground/50" strokeWidth={1} />
                <p className="font-medium">Документів не знайдено</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  За заданими параметрами немає документів. Змініть критерії пошуку та
                  спробуйте ще раз.
                </p>
              </div>
            ) : (
              <DataTable
                columns={buildDocumentSearchColumns({
                  linkTo: (id) => ({ pathname: `/documents/search/${id}` }),
                  punktNameById,
                })}
                data={results}
                enablePagination={false}
                footer={
                  nextPage !== null && (
                    <div className="flex justify-center border-t p-3">
                      <Button
                        disabled={isLoadingMore || searchParamsChanged}
                        onClick={handleLoadMore}
                        title={
                          searchParamsChanged
                            ? "Параметри пошуку змінилися — виконайте пошук ще раз"
                            : undefined
                        }
                        variant="outline"
                      >
                        {isLoadingMore ? "Завантаження…" : "Завантажити ще"}
                      </Button>
                    </div>
                  )
                }
                getRowId={(row) => row.id}
                isLoading={isSearching}
                tableClassName="table-fixed"
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
